import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Banknote, Clock, AlertTriangle, CheckCircle, 
  Upload, FileText, RefreshCw, ChevronRight,
  Calendar, Shield, AlertCircle
} from 'lucide-react';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

export default function LandlordDeposits() {
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${getToken()}` };
      
      const [statsRes, casesRes] = await Promise.all([
        fetch(`${API_URL}/api/deposits/stats`, { headers }),
        fetch(`${API_URL}/api/deposits/cases`, { headers })
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (casesRes.ok) {
        const data = await casesRes.json();
        setCases(data.cases || []);
      }
    } catch (error) {
      console.error('Failed to load deposits:', error);
      toast.error('Failed to load deposit data');
    } finally {
      setLoading(false);
    }
  };

  const lodgeDeposit = async (caseId) => {
    try {
      const response = await fetch(`${API_URL}/api/deposits/cases/${caseId}/lodge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        toast.success('Deposit submitted for lodgement');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to lodge deposit');
      }
    } catch (error) {
      toast.error('Failed to lodge deposit');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-50';
      case 'pending': case 'submitted': return 'text-yellow-600 bg-yellow-50';
      case 'failed': case 'dispute_open': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'pending': case 'submitted': return <Clock className="w-4 h-4" />;
      case 'failed': case 'dispute_open': return <AlertTriangle className="w-4 h-4" />;
      default: return <Banknote className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Banknote className="w-7 h-7 text-green-600" />
              Deposit Management
            </h1>
            <p className="text-gray-600 mt-1">Track and manage tenant deposits</p>
          </div>
          <Button onClick={loadData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="p-4">
              <div className="text-sm text-gray-500">Total Cases</div>
              <div className="text-2xl font-bold">{stats.total_cases}</div>
            </Card>
            <Card className="p-4 border-green-200 bg-green-50">
              <div className="text-sm text-green-600">Protected</div>
              <div className="text-2xl font-bold text-green-700">{stats.protected}</div>
            </Card>
            <Card className="p-4 border-yellow-200 bg-yellow-50">
              <div className="text-sm text-yellow-600">Pending</div>
              <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            </Card>
            <Card className="p-4 border-red-200 bg-red-50">
              <div className="text-sm text-red-600">In Dispute</div>
              <div className="text-2xl font-bold text-red-700">{stats.in_dispute}</div>
            </Card>
            <Card className="p-4 border-orange-200 bg-orange-50">
              <div className="text-sm text-orange-600">Deadline Soon</div>
              <div className="text-2xl font-bold text-orange-700">{stats.approaching_deadline}</div>
            </Card>
          </div>
        )}

        {/* Total Amount */}
        {stats && stats.total_amount_held > 0 && (
          <Card className="p-6 mb-8 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg opacity-90">Total Deposits Protected</div>
                <div className="text-4xl font-bold">
                  £{stats.total_amount_held.toLocaleString()}
                </div>
              </div>
              <Shield className="w-16 h-16 opacity-50" />
            </div>
          </Card>
        )}

        {/* Cases List */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-semibold">Deposit Cases</h2>
          </div>
          
          {cases.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Banknote className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No deposit cases yet</p>
              <p className="text-sm mt-1">Deposit cases are created when tenancies start</p>
            </div>
          ) : (
            <div className="divide-y">
              {cases.map((depositCase) => (
                <div 
                  key={depositCase.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${getStatusColor(depositCase.status)}`}>
                        {getStatusIcon(depositCase.status)}
                      </div>
                      <div>
                        <div className="font-medium">
                          £{depositCase.amount?.toLocaleString() || '0'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {depositCase.jurisdiction?.toUpperCase()} • {depositCase.scheme_provider_code}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Deadline warning */}
                      {depositCase.days_remaining !== undefined && depositCase.status !== 'confirmed' && (
                        <div className={`text-sm ${
                          depositCase.days_remaining <= 7 ? 'text-red-600' : 
                          depositCase.days_remaining <= 14 ? 'text-orange-600' : 'text-gray-600'
                        }`}>
                          {depositCase.is_overdue ? (
                            <span className="flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              OVERDUE
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {depositCase.days_remaining} days left
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Status badge */}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(depositCase.status)}`}>
                        {depositCase.status?.replace('_', ' ')}
                      </span>
                      
                      {/* Actions */}
                      {depositCase.status === 'pending' && (
                        <Button 
                          size="sm" 
                          onClick={() => lodgeDeposit(depositCase.id)}
                        >
                          Lodge Now
                        </Button>
                      )}
                      
                      {depositCase.scheme_reference && (
                        <div className="text-xs text-gray-500">
                          Ref: {depositCase.scheme_reference}
                        </div>
                      )}
                      
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  
                  {/* Evidence files */}
                  {depositCase.evidence_files?.length > 0 && (
                    <div className="mt-3 flex gap-2">
                      {depositCase.evidence_files.map((file, idx) => (
                        <span 
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs"
                        >
                          <FileText className="w-3 h-3" />
                          {file.evidence_type}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Info Card */}
        <Card className="mt-6 p-6 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Deposit Protection Requirements
          </h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>England & Wales:</strong> Deposits must be protected within 30 days of receipt</li>
            <li>• <strong>Scotland:</strong> Deposits must be protected within 30 working days</li>
            <li>• Prescribed information must be provided to tenants</li>
            <li>• Failure to comply can result in penalties of 1-3x the deposit amount</li>
          </ul>
        </Card>
      </div>
    </DashboardLayout>
  );
}
